import type {
  AdjustStockRequest,
  AuditEntryDto,
  BalanceDto,
  BatchDto,
  CancelBatchRequest,
  Capability,
  CapabilityDto,
  ChangePasswordRequest,
  CloseBatchRequest,
  CompleteBatchRequest,
  CreateBatchRequest,
  CreateItemRequest,
  CreateLocationRequest,
  CreateRecipeRequest,
  CreateRecoveryRequest,
  CreateRoleRequest,
  CreateSupplierRequest,
  CreateUserRequest,
  GstCaptchaDto,
  GstLookupDetails,
  GstLookupRequest,
  IssueStockRequest,
  ItemDto,
  ItemKind,
  LocationDto,
  LocationType,
  LoginResult,
  LotDetailDto,
  LotDto,
  LowStockRow,
  Milestone,
  MovementDto,
  MovementSummaryReport,
  Paginated,
  QualityCheckDto,
  QualityCheckRequest,
  ReceiveStockRequest,
  RecipeDto,
  ReconciliationReport,
  RecordConsumptionRequest,
  RecoveryCreditReport,
  RecoveryDto,
  RoleDto,
  ScrapStockRequest,
  SectionDto,
  SessionInfo,
  StockValuationReport,
  SuggestedCode,
  SupplierDto,
  TraceGraph,
  TransferStockRequest,
  UpdateItemRequest,
  UpdateLocationRequest,
  UpdateProfileRequest,
  UpdateRecipeRequest,
  UpdateRoleRequest,
  UpdateSupplierRequest,
  UpdateUserRequest,
  UserDto,
} from '@gsls/shared';

/**
 * The web app always calls the API on the same origin:
 *  - production: nginx proxies /api -> gsls-api:3000
 *  - dev in Docker: the Vite dev server proxies /api -> dev-api:3000
 * Because it is same-origin, the session cookie rides along automatically and
 * no CORS configuration is involved.
 */
export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

export const API_PATHS = {
  health: '/health',
  ready: '/ready',
  meta: '/meta',
  me: '/auth/me',
  login: '/auth/login',
  logout: '/auth/logout',
  changePassword: '/auth/change-password',
  items: '/items',
  locations: '/locations',
  suppliers: '/suppliers',
  gstCaptcha: '/suppliers/gst-captcha',
  gstLookup: '/suppliers/gst-lookup',
  users: '/users',
  roles: '/roles',
  capabilities: '/capabilities',
  sections: '/sections',
  balances: '/inventory/balances',
  movements: '/inventory/movements',
  lots: '/inventory/lots',
  lowStock: '/inventory/low-stock',
  reconcile: '/inventory/reconcile',
  receipts: '/inventory/receipts',
  issues: '/inventory/issues',
  transfers: '/inventory/transfers',
  adjustments: '/inventory/adjustments',
  scrap: '/inventory/scrap',
  recipes: '/recipes',
  batches: '/batches',
  recoveries: '/recoveries',
  qualityChecks: '/quality-checks',
  trace: '/trace',
  reports: '/reports',
  audit: '/audit',
} as const;

/** A report that can be downloaded as CSV as well as displayed. */
export interface ReportParams {
  from?: string;
  to?: string;
  itemId?: string;
  locationId?: string;
}

/** One lot waiting for a quality decision. */
export interface PendingLot {
  lotId: string;
  lotCode: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  baseUom: string;
  origin: string;
  onHoldSince: string;
  onHand: string;
  supplierName: string | null;
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Not signed in (or the session expired). */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Signed in, but the role is not allowed to do this. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * The most specific thing the API said about a failure.
 *
 * A validation failure's own `message` is the uselessly generic "Validation
 * failed"; the useful text is in `details`, one entry per problem — "'27AAPFU0939F1ZJ'
 * must end in 'V'" rather than "something was wrong". Wherever the API takes the
 * trouble to name what is wrong, showing it is the difference between an error
 * and an explanation, so this reads whatever shape the details arrived in and
 * falls back to the message only when there is nothing better.
 */
export function describeApiError(error: unknown): string {
  if (!(error instanceof ApiError)) return '';

  const raw = error.details as { issues?: unknown } | unknown[] | undefined;
  const candidates = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray(raw.issues)
      ? (raw.issues as unknown[])
      : [];

  const messages = candidates
    .map((candidate) => {
      const { path, message } = candidate as { path?: unknown; message?: unknown };
      if (typeof message !== 'string' || message.length === 0) return null;
      // Naming the field turns "String must contain at least 8 character(s)" into
      // "password: String must contain…", which is what makes it actionable.
      return typeof path === 'string' && path.length > 0 ? `${path}: ${message}` : message;
    })
    .filter((message): message is string => message !== null);

  return messages.length > 0 ? messages.join(' · ') : error.message;
}

export interface Health {
  status: 'ok';
  service: string;
  version: string;
  uptimeSeconds: number;
  timestamp: string;
}

export interface Readiness {
  status: 'ready' | 'degraded';
  database: 'up' | 'down';
  timestamp: string;
}

export interface Meta {
  project: string;
  version: string;
  milestones: Milestone[];
  capabilities: {
    inventory: boolean;
    productionBatches: boolean;
    recoveryTagging: boolean;
    traceability: boolean;
  };
  timestamp: string;
}

export interface ListParams {
  search?: string;
  includeInactive?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * Filters accepted by the ledger read endpoints. This mirrors what the API
 * actually reads (see `movementQuery` / `lotQuery` in @gsls/shared) so the web
 * app can use the same filters the server already supports — notably the date
 * range on movements and expiry/status on lots, which the dashboard needs.
 */
export interface InventoryQuery {
  itemId?: string;
  lotId?: string;
  locationId?: string;
  type?: string;
  /** ISO date-time bounds on `occurredAt`. Movements only. */
  from?: string;
  to?: string;
  /** Lots only: restrict to a status or origin. */
  status?: string;
  origin?: string;
  /** Lots only: include lots whose expiry is on or before this instant. */
  expiringBefore?: string;
  search?: string;
  hideEmpty?: boolean;
  includeEmpty?: boolean;
  limit?: number;
  offset?: number;
}

/** Filters accepted by the recipe list. */
export interface RecipeQuery {
  itemId?: string;
  status?: string;
  search?: string;
  /** Only the newest version of each recipe code. */
  latestOnly?: boolean;
  limit?: number;
  offset?: number;
}

/** Filters accepted by the batch list. */
export interface BatchQuery {
  status?: string;
  itemId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const hasBody = init?.body !== undefined;

  const response = await fetch(`${API_BASE}${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: {
      accept: 'application/json',
      ...(hasBody ? { 'content-type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });

  const body: unknown = response.status === 204 ? null : await response.json().catch(() => null);

  if (!response.ok) {
    const apiError = (body as ApiErrorBody | null)?.error;
    throw new ApiError(
      response.status,
      apiError?.code ?? 'http_error',
      apiError?.message ?? `Request failed with status ${response.status}`,
      apiError?.details,
    );
  }

  return body as T;
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const serialised = search.toString();
  return serialised ? `?${serialised}` : '';
}

function json(body: unknown): RequestInit {
  return { body: JSON.stringify(body) };
}

export const api = {
  health: () => request<Health>(API_PATHS.health),
  readiness: () => request<Readiness>(API_PATHS.ready),
  meta: () => request<Meta>(API_PATHS.meta),

  auth: {
    me: () => request<SessionInfo>(API_PATHS.me),
    login: (email: string, password: string) =>
      request<LoginResult>(API_PATHS.login, {
        method: 'POST',
        ...json({ email, password }),
      }),
    logout: () => request<null>(API_PATHS.logout, { method: 'POST' }),
    /** Edit your own display name. Returns the refreshed session. */
    updateProfile: (body: UpdateProfileRequest) =>
      request<SessionInfo>(API_PATHS.me, { method: 'PATCH', ...json(body) }),
    /** Change your own password. Revokes every session, this one included. */
    changePassword: (body: ChangePasswordRequest) =>
      request<null>(API_PATHS.changePassword, { method: 'POST', ...json(body) }),
  },

  items: {
    list: (params: ListParams = {}) =>
      request<Paginated<ItemDto>>(`${API_PATHS.items}${query({ ...params })}`),
    /** Server-side suggestion: the UI never invents a code. */
    suggestCode: (params: { kind: ItemKind; name: string }) =>
      request<SuggestedCode>(`${API_PATHS.items}/suggest-code${query({ ...params })}`),
    create: (body: CreateItemRequest) =>
      request<ItemDto>(API_PATHS.items, { method: 'POST', ...json(body) }),
    update: (id: string, body: UpdateItemRequest) =>
      request<ItemDto>(`${API_PATHS.items}/${id}`, { method: 'PATCH', ...json(body) }),
  },

  locations: {
    list: (params: ListParams = {}) =>
      request<Paginated<LocationDto>>(`${API_PATHS.locations}${query({ ...params })}`),
    suggestCode: (params: { type: LocationType; name: string }) =>
      request<SuggestedCode>(`${API_PATHS.locations}/suggest-code${query({ ...params })}`),
    create: (body: CreateLocationRequest) =>
      request<LocationDto>(API_PATHS.locations, { method: 'POST', ...json(body) }),
    update: (id: string, body: UpdateLocationRequest) =>
      request<LocationDto>(`${API_PATHS.locations}/${id}`, { method: 'PATCH', ...json(body) }),
  },

  suppliers: {
    list: (params: ListParams = {}) =>
      request<Paginated<SupplierDto>>(`${API_PATHS.suppliers}${query({ ...params })}`),
    suggestCode: (params: { name: string }) =>
      request<SuggestedCode>(`${API_PATHS.suppliers}/suggest-code${query({ ...params })}`),
    create: (body: CreateSupplierRequest) =>
      request<SupplierDto>(API_PATHS.suppliers, { method: 'POST', ...json(body) }),
    update: (id: string, body: UpdateSupplierRequest) =>
      request<SupplierDto>(`${API_PATHS.suppliers}/${id}`, { method: 'PATCH', ...json(body) }),
    /** A fresh captcha image and the cookie the lookup must echo back. */
    gstCaptcha: () => request<GstCaptchaDto>(API_PATHS.gstCaptcha),
    /** Reads a taxpayer from the GST portal: a GSTIN plus the captcha a person read off the image. */
    gstLookup: (body: GstLookupRequest) =>
      request<GstLookupDetails>(API_PATHS.gstLookup, { method: 'POST', ...json(body) }),
  },

  users: {
    list: (params: ListParams = {}) =>
      request<Paginated<UserDto>>(`${API_PATHS.users}${query({ ...params })}`),
    create: (body: CreateUserRequest) =>
      request<UserDto>(API_PATHS.users, { method: 'POST', ...json(body) }),
    update: (id: string, body: UpdateUserRequest) =>
      request<UserDto>(`${API_PATHS.users}/${id}`, { method: 'PATCH', ...json(body) }),
  },

  roles: {
    list: (params: { search?: string } = {}) =>
      request<Paginated<RoleDto>>(`${API_PATHS.roles}${query({ ...params })}`),
    /** The capabilities an administrator may grant, described for the editor. */
    capabilities: () => request<{ items: CapabilityDto[] }>(API_PATHS.capabilities),
    /** The screens a workspace may present, for the same editor. */
    sections: () => request<{ items: SectionDto[] }>(API_PATHS.sections),
    create: (body: CreateRoleRequest) =>
      request<RoleDto>(API_PATHS.roles, { method: 'POST', ...json(body) }),
    update: (id: string, body: UpdateRoleRequest) =>
      request<RoleDto>(`${API_PATHS.roles}/${id}`, { method: 'PATCH', ...json(body) }),
    remove: (id: string) => request<null>(`${API_PATHS.roles}/${id}`, { method: 'DELETE' }),
  },

  inventory: {
    balances: (params: InventoryQuery = {}) =>
      request<Paginated<BalanceDto>>(`${API_PATHS.balances}${query({ ...params })}`),
    movements: (params: InventoryQuery = {}) =>
      request<Paginated<MovementDto>>(`${API_PATHS.movements}${query({ ...params })}`),
    lots: (params: InventoryQuery = {}) =>
      request<Paginated<LotDto>>(`${API_PATHS.lots}${query({ ...params })}`),
    lot: (id: string) => request<LotDetailDto>(`${API_PATHS.lots}/${id}`),
    /** Server-side suggestion, so the UI never invents a lot code. */
    suggestLotCode: (itemId: string) =>
      request<SuggestedCode>(`${API_PATHS.lots}/suggest-code${query({ itemId })}`),
    lowStock: () => request<{ items: LowStockRow[] }>(API_PATHS.lowStock),
    reconcile: () => request<ReconciliationReport>(API_PATHS.reconcile),

    receive: (body: ReceiveStockRequest) =>
      request<{ lot: LotDto; movement: MovementDto }>(API_PATHS.receipts, {
        method: 'POST',
        ...json(body),
      }),
    issue: (body: IssueStockRequest) =>
      request<{ movement: MovementDto }>(API_PATHS.issues, { method: 'POST', ...json(body) }),
    transfer: (body: TransferStockRequest) =>
      request<{ movements: MovementDto[]; lot: LotDto }>(API_PATHS.transfers, {
        method: 'POST',
        ...json(body),
      }),
    adjust: (body: AdjustStockRequest) =>
      request<{ movement: MovementDto }>(API_PATHS.adjustments, {
        method: 'POST',
        ...json(body),
      }),
    scrap: (body: ScrapStockRequest) =>
      request<{ movement: MovementDto }>(API_PATHS.scrap, { method: 'POST', ...json(body) }),
    /** Corrections are posted, never edited — see the ledger's append-only rule. */
    reverse: (movementId: string, reason: string) =>
      request<{ movements: MovementDto[] }>(`${API_PATHS.movements}/${movementId}/reverse`, {
        method: 'POST',
        ...json({ reason }),
      }),
  },

  recipes: {
    list: (params: RecipeQuery = {}) =>
      request<Paginated<RecipeDto>>(`${API_PATHS.recipes}${query({ ...params })}`),
    get: (id: string) => request<RecipeDto>(`${API_PATHS.recipes}/${id}`),
    approvedForItem: (itemId: string) =>
      request<{ recipe: RecipeDto }>(`${API_PATHS.recipes}/approved${query({ itemId })}`),
    suggestCode: (params: { name: string }) =>
      request<SuggestedCode>(`${API_PATHS.recipes}/suggest-code${query({ ...params })}`),
    create: (body: CreateRecipeRequest) =>
      request<RecipeDto>(API_PATHS.recipes, { method: 'POST', ...json(body) }),
    update: (id: string, body: UpdateRecipeRequest) =>
      request<RecipeDto>(`${API_PATHS.recipes}/${id}`, { method: 'PATCH', ...json(body) }),
    approve: (id: string, note?: string) =>
      request<RecipeDto>(`${API_PATHS.recipes}/${id}/approve`, {
        method: 'POST',
        ...json(note ? { note } : {}),
      }),
    /** Revising an approved recipe means a new version, never an edit. */
    newVersion: (id: string) =>
      request<RecipeDto>(`${API_PATHS.recipes}/${id}/new-version`, { method: 'POST' }),
    retire: (id: string) => request<RecipeDto>(`${API_PATHS.recipes}/${id}/retire`, { method: 'POST' }),
    remove: (id: string) => request<null>(`${API_PATHS.recipes}/${id}`, { method: 'DELETE' }),
  },

  batches: {
    list: (params: BatchQuery = {}) =>
      request<Paginated<BatchDto>>(`${API_PATHS.batches}${query({ ...params })}`),
    get: (id: string) => request<BatchDto>(`${API_PATHS.batches}/${id}`),
    /** The product and the site both go into the code, so the suggestion needs each. */
    suggestCode: (itemId: string, locationId: string) =>
      request<SuggestedCode>(`${API_PATHS.batches}/suggest-code${query({ itemId, locationId })}`),
    create: (body: CreateBatchRequest) =>
      request<BatchDto>(API_PATHS.batches, { method: 'POST', ...json(body) }),
    start: (id: string) => request<BatchDto>(`${API_PATHS.batches}/${id}/start`, { method: 'POST' }),
    /** Posts the draw to the inventory ledger immediately. */
    consume: (id: string, body: RecordConsumptionRequest) =>
      request<BatchDto>(`${API_PATHS.batches}/${id}/consumptions`, {
        method: 'POST',
        ...json(body),
      }),
    complete: (id: string, body: CompleteBatchRequest) =>
      request<BatchDto>(`${API_PATHS.batches}/${id}/complete`, { method: 'POST', ...json(body) }),
    close: (id: string, body: CloseBatchRequest) =>
      request<BatchDto>(`${API_PATHS.batches}/${id}/close`, { method: 'POST', ...json(body) }),
    cancel: (id: string, body: CancelBatchRequest) =>
      request<BatchDto>(`${API_PATHS.batches}/${id}/cancel`, { method: 'POST', ...json(body) }),
  },

  recoveries: {
    list: (params: { itemId?: string; batchId?: string; search?: string; limit?: number } = {}) =>
      request<Paginated<RecoveryDto>>(`${API_PATHS.recoveries}${query({ ...params })}`),
    suggestCode: () => request<SuggestedCode>(`${API_PATHS.recoveries}/suggest-code`),
    create: (body: CreateRecoveryRequest) =>
      request<RecoveryDto>(API_PATHS.recoveries, { method: 'POST', ...json(body) }),
  },

  quality: {
    list: (params: { lotId?: string; status?: string; limit?: number } = {}) =>
      request<Paginated<QualityCheckDto>>(`${API_PATHS.qualityChecks}${query({ ...params })}`),
    /** Lots on hold, oldest first. */
    pending: () => request<{ items: PendingLot[] }>(`${API_PATHS.qualityChecks}/pending`),
    decide: (body: QualityCheckRequest) =>
      request<QualityCheckDto>(API_PATHS.qualityChecks, { method: 'POST', ...json(body) }),
    reopen: (lotId: string, reason: string) =>
      request<QualityCheckDto>(`${API_PATHS.qualityChecks}/reopen`, {
        method: 'POST',
        ...json({ lotId, reason }),
      }),
  },

  trace: {
    /** Genealogy of a lot: what it came from and where it went. */
    lot: (lotId: string, params: { direction?: string; depth?: number } = {}) =>
      request<TraceGraph>(`${API_PATHS.trace}/lots/${lotId}${query({ ...params })}`),
    batch: (batchId: string, params: { direction?: string; depth?: number } = {}) =>
      request<TraceGraph>(`${API_PATHS.trace}/batches/${batchId}${query({ ...params })}`),
  },

  reports: {
    stockValuation: (params: ReportParams = {}) =>
      request<StockValuationReport>(`${API_PATHS.reports}/stock-valuation${query({ ...params })}`),
    movements: (params: ReportParams = {}) =>
      request<MovementSummaryReport>(`${API_PATHS.reports}/movements${query({ ...params })}`),
    recoveryCredit: (params: ReportParams = {}) =>
      request<RecoveryCreditReport>(`${API_PATHS.reports}/recovery-credit${query({ ...params })}`),
  },

  audit: {
    list: (params: {
      actorUserId?: string;
      entityType?: string;
      entityId?: string;
      action?: string;
      from?: string;
      to?: string;
      limit?: number;
      offset?: number;
    } = {}) => request<Paginated<AuditEntryDto>>(`${API_PATHS.audit}${query({ ...params })}`),
    actions: () => request<{ actions: string[] }>(`${API_PATHS.audit}/actions`),
  },
};

/** Same-origin CSV download for a report, so the session cookie rides along. */
export function reportCsvUrl(
  report: 'stock-valuation' | 'movements' | 'recovery-credit',
  params: ReportParams = {},
): string {
  return `${API_BASE}${API_PATHS.reports}/${report}${query({ ...params, format: 'csv' })}`;
}

export type { Capability, LoginResult, SessionInfo, Paginated };
